/**
 * useSpecialtySets — GET /v1/admin/specialty-sets?year=
 *
 * Catalog of specialty-set codes uploaded for a performance year. New in
 * backend v0.4.1 (added specifically to unblock the V1.2.1 controlled-
 * dropdown UX on the AdminClinicsPage edit modal).
 *
 * The endpoint returns one row per (code, collection_type) — so a set
 * offered under multiple collection_types appears more than once. Consumers
 * must filter by the clinic's `submission_method` before keying the
 * dropdown on `code`. See `dedupeForSubmissionMethod` below — the helper
 * that does this. The dropdown should never render duplicate options.
 *
 * Convention (matches v0.4.0 IA/PI lists): 200 + empty array for a year
 * with no uploaded sets (not 404).
 *
 * Caching: this catalog rarely changes — it's only re-uploaded when CMS
 * ships a new Appendix B CSV. A 5-minute staleTime is appropriate; the
 * uploadSpecialtySetMeasures mutation already invalidates the dashboard
 * tree but we explicitly invalidate this catalog too when an upload
 * happens (see useUploadSpecialtySetMeasures).
 */
import { useQuery } from "@tanstack/react-query";
import { getApiClient } from "@/api/client";
import type {
  SpecialtySetListResponse,
  SpecialtySetSummary,
} from "@/api/models";
import { queryKeys } from "./queryKeys";

export interface UseSpecialtySetsOptions {
  /** Performance year. Backend defaults to current year if omitted. */
  year?: number;
  enabled?: boolean;
}

export function useSpecialtySets(options: UseSpecialtySetsOptions = {}) {
  const { year, enabled } = options;
  return useQuery({
    queryKey: queryKeys.specialtySets.list(year),
    queryFn: async (): Promise<SpecialtySetListResponse> => {
      const params: Record<string, number> = {};
      if (year !== undefined) params.year = year;
      const { data } = await getApiClient().get<SpecialtySetListResponse>(
        "/v1/admin/specialty-sets",
        { params: Object.keys(params).length > 0 ? params : undefined },
      );
      return data;
    },
    staleTime: 5 * 60_000, // 5 minutes — catalog rarely changes
    enabled: enabled ?? true,
  });
}

/**
 * Filter the catalog to entries that match the given collection_type
 * (typically the clinic's submission_method). After filtering, `code`
 * IS unique — the dropdown can key on it.
 *
 * Returns an empty array if `submissionMethod` is null/empty.
 */
export function filterCatalogForCollectionType(
  sets: readonly SpecialtySetSummary[] | undefined,
  submissionMethod: string | null | undefined,
): SpecialtySetSummary[] {
  if (!sets || !submissionMethod) return [];
  const trimmed = submissionMethod.trim();
  if (trimmed === "") return [];
  return sets.filter(
    (s) => s.collection_type === trimmed,
  );
}
